/**
 * DashVerification — Onboarding-to-Verification journey.
 *
 * Guides the business owner through the required steps to become a
 * verified CARTAL Business:
 *   1) Business basic info
 *   2) Official contact (email + phone)
 *   3) Required documents (CR mandatory + optional)
 *   4) Submit for review
 *   5) Wait for admin approval
 *
 * Progress is persisted on the server as a draft; the user can leave
 * and come back at any time. Operational features remain hard-gated
 * (see BusinessDashboardPage) until admin approves.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ClipboardCheck, Clock, AlertTriangle, Upload, Trash2, Send, Check,
  FileText, Info, MailCheck, Building2,
} from "lucide-react";
import { toast } from "sonner";

import { apiClient, formatApiError } from "../../../lib/api";
import { useI18n } from "../../../lib/i18n";
import {
  STATUS_META, TONE_COLORS, REQUIRED_DOCS, RECOMMENDED_DOCS, DOC_LABELS,
  CATEGORY_REQUIRED_DOCS, computeChecklist,
} from "../verificationStatus";

const EDITABLE_STATES = new Set(["draft", "needs_more_docs", "needs_reverify", "rejected"]);
const UPLOAD_STATES = new Set(["draft", "needs_more_docs", "needs_reverify", "rejected", "under_review"]);

export default function DashVerification({ businessId, home }) {
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingType, setUploadingType] = useState(null);
  const fileInputsRef = useRef({});

  const load = useCallback(async () => {
    try {
      const { data: d } = await apiClient.get(`/platform/verification/businesses/${businessId}`);
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load verification");
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const submission = data?.submission || null;
  const documents = data?.documents || [];
  const status = submission?.status || "draft";
  const meta = STATUS_META[status] || STATUS_META.draft;
  const badgeColor = TONE_COLORS[meta.tone];
  const editable = EDITABLE_STATES.has(status);
  const canUpload = UPLOAD_STATES.has(status);

  const business = home?.business || {};
  const checklist = useMemo(
    () => computeChecklist(business, submission, documents),
    [business, submission, documents],
  );
  const canSubmit = checklist.steps.slice(0, 3).every((s) => s.done) && editable;

  /* ─── Contact form ────────────────────────────────────────────────*/
  const [contact, setContact] = useState({
    official_email: "",
    official_phone: "",
    official_address_ar: "",
    website: "",
    representative_name: "",
    representative_national_id: "",
    representative_phone: "",
  });
  useEffect(() => {
    if (submission?.contact) {
      setContact((prev) => ({ ...prev, ...submission.contact }));
    }
  }, [submission?.contact]);

  const saveContact = async () => {
    setSaving(true);
    try {
      const { data: d } = await apiClient.patch(
        `/platform/verification/businesses/${businessId}/contact`,
        { contact },
      );
      setData((prev) => ({ ...prev, submission: d.submission }));
      toast.success(lang === "ar" ? "تم حفظ بيانات الاتصال" : "Contact info saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  /* ─── Upload ──────────────────────────────────────────────────────*/
  const uploadDoc = async (docType, file) => {
    if (!file) return;
    setUploadingType(docType);
    try {
      const form = new FormData();
      form.append("doc_type", docType);
      form.append("file", file);
      const label = DOC_LABELS[docType];
      if (label) {
        form.append("label_ar", label.ar);
        form.append("label_en", label.en);
      }
      await apiClient.post(
        `/platform/verification/businesses/${businessId}/documents`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(lang === "ar" ? "تم رفع الوثيقة" : "Document uploaded");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingType(null);
      const inp = fileInputsRef.current[docType];
      if (inp) inp.value = "";
    }
  };

  const deleteDoc = async (docId) => {
    try {
      await apiClient.delete(`/platform/verification/businesses/${businessId}/documents/${docId}`);
      toast.success(lang === "ar" ? "تم حذف الوثيقة" : "Document deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed");
    }
  };

  /* ─── Submit ──────────────────────────────────────────────────────*/
  const submit = async () => {
    setSubmitting(true);
    try {
      const { data: d } = await apiClient.post(
        `/platform/verification/businesses/${businessId}/submit`,
      );
      setData((prev) => ({ ...prev, submission: d.submission }));
      toast.success(lang === "ar" ? "تم إرسال الطلب لإدارة كرتال" : "Submitted for review");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center" data-testid="dash-verification-loading">
        <div className="w-6 h-6 border-2 border-[#c9a55c] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const uploadedByType = new Map();
  documents.forEach((d) => {
    if (!uploadedByType.has(d.doc_type)) uploadedByType.set(d.doc_type, []);
    uploadedByType.get(d.doc_type).push(d);
  });

  return (
    <div className="space-y-6" data-testid="dash-verification">
      {/* ── Status hero ─────────────────────────────────────────── */}
      <div
        className="rounded-2xl p-6 border"
        style={{ background: "linear-gradient(135deg,#0a0a0a,#1f1614)", borderColor: "transparent" }}
        data-testid="verification-status-hero"
      >
        <div className="flex items-start gap-4 flex-wrap">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
            style={{ background: `${badgeColor}22`, border: `1px solid ${badgeColor}66` }}
          >
            {status === "approved" ? <ClipboardCheck className="w-6 h-6" style={{ color: badgeColor }} /> :
             ["rejected", "revoked", "suspended"].includes(status) ?
                <AlertTriangle className="w-6 h-6" style={{ color: badgeColor }} /> :
             <Clock className="w-6 h-6" style={{ color: badgeColor }} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-[#f7eecd]/60 font-semibold">
              {lang === "ar" ? "حالة التوثيق" : "Verification status"}
            </div>
            <div className="text-2xl font-bold text-[#f7eecd] mt-1" data-testid="verification-status-label">
              {lang === "ar" ? meta.ar : meta.en}
            </div>
            <div className="text-[13px] text-[#f7eecd]/70 mt-2 leading-relaxed max-w-2xl">
              {lang === "ar" ? meta.description_ar : meta.description_en}
            </div>
            {submission?.decision_reason && ["needs_more_docs", "rejected", "suspended", "revoked"].includes(status) && (
              <div
                className="mt-4 rounded-lg px-3 py-2 text-[13px]"
                style={{ background: "rgba(220,38,38,0.12)", color: "#fecaca", border: "1px solid rgba(220,38,38,0.35)" }}
                data-testid="verification-decision-reason"
              >
                <span className="font-semibold">{lang === "ar" ? "ملاحظة الإدارة: " : "Admin note: "}</span>
                {submission.decision_reason}
              </div>
            )}
          </div>

          {/* Progress ring / counter */}
          <div className="text-end">
            <div className="text-[11px] uppercase tracking-widest text-[#f7eecd]/60">
              {lang === "ar" ? "التقدّم" : "Progress"}
            </div>
            <div className="text-3xl font-bold text-[#e8c873]" data-testid="verification-progress">
              {checklist.completed}/{checklist.total}
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-5 h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${(checklist.completed / checklist.total) * 100}%`,
              background: "linear-gradient(90deg,#e8c873,#c9a55c)",
            }}
          />
        </div>
      </div>

      {/* ── Checklist steps ─────────────────────────────────────── */}
      <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-5" data-testid="verification-checklist">
        <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-4">
          {lang === "ar" ? "خطوات استكمال التوثيق" : "Onboarding checklist"}
        </div>
        <ol className="space-y-3">
          {checklist.steps.map((s, i) => (
            <li key={s.key} className="flex items-center gap-3" data-testid={`checklist-step-${s.key}`}>
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-bold shrink-0"
                style={{
                  background: s.done ? "#16a34a" : "#f0eee7",
                  color: s.done ? "#fff" : "#8a8a8a",
                }}
              >
                {s.done ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-semibold ${s.done ? "text-[#525252] line-through" : "text-[#0a0a0a]"}`}>
                  {lang === "ar" ? s.labelAr : s.labelEn}
                </div>
              </div>
              {!s.done && checklist.nextStep === s.key && (
                <span
                  className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full"
                  style={{ background: "#fef3c7", color: "#8a6f2e" }}
                  data-testid="checklist-next-badge"
                >
                  {lang === "ar" ? "التالي" : "next"}
                </span>
              )}
            </li>
          ))}
        </ol>
      </div>

      {/* ── Step 1: Business info (link to profile) ─────────────── */}
      <StepCard
        num={1}
        titleAr="بيانات النشاط الأساسية"
        titleEn="Business information"
        done={checklist.steps[0].done}
        testid="verification-step-business"
      >
        <div className="grid sm:grid-cols-2 gap-3 text-[13px]">
          <InfoRow label={lang === "ar" ? "الاسم بالعربية" : "Name (AR)"} value={business.name_ar} />
          <InfoRow label={lang === "ar" ? "الاسم بالإنجليزية" : "Name (EN)"} value={business.name_en} />
          <InfoRow label={lang === "ar" ? "الفئة" : "Category"} value={business.category} />
          <InfoRow label={lang === "ar" ? "المدينة" : "City"} value={business.profile?.city} />
        </div>
        <div className="mt-4 text-[12px] text-[#8a8a8a]">
          {lang === "ar"
            ? "لتعديل بيانات النشاط أو الشعار، انتقل إلى تبويب «الملف التعريفي»."
            : "To edit business info or logo, use the Profile tab."}
        </div>
      </StepCard>

      {/* ── Step 2: Contact ─────────────────────────────────────── */}
      <StepCard
        num={2}
        titleAr="بيانات الاتصال الرسمية"
        titleEn="Official contact"
        done={checklist.steps[1].done}
        testid="verification-step-contact"
      >
        <div className="grid sm:grid-cols-2 gap-3">
          <Field
            label={lang === "ar" ? "البريد الرسمي *" : "Official email *"}
            value={contact.official_email}
            onChange={(v) => setContact({ ...contact, official_email: v })}
            disabled={!editable}
            type="email"
            testid="contact-email"
          />
          <Field
            label={lang === "ar" ? "الهاتف الرسمي *" : "Official phone *"}
            value={contact.official_phone}
            onChange={(v) => setContact({ ...contact, official_phone: v })}
            disabled={!editable}
            testid="contact-phone"
          />
          <Field
            label={lang === "ar" ? "العنوان بالعربية" : "Address (AR)"}
            value={contact.official_address_ar}
            onChange={(v) => setContact({ ...contact, official_address_ar: v })}
            disabled={!editable}
            testid="contact-address"
          />
          <Field
            label={lang === "ar" ? "الموقع الإلكتروني" : "Website"}
            value={contact.website}
            onChange={(v) => setContact({ ...contact, website: v })}
            disabled={!editable}
            testid="contact-website"
          />
          <Field
            label={lang === "ar" ? "اسم الممثل المفوّض" : "Authorized representative"}
            value={contact.representative_name}
            onChange={(v) => setContact({ ...contact, representative_name: v })}
            disabled={!editable}
            testid="contact-rep-name"
          />
          <Field
            label={lang === "ar" ? "هوية الممثل الوطنية" : "Rep national ID"}
            value={contact.representative_national_id}
            onChange={(v) => setContact({ ...contact, representative_national_id: v })}
            disabled={!editable}
            testid="contact-rep-id"
          />
        </div>
        {editable && (
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={saveContact}
              disabled={saving}
              className="px-4 py-2 rounded-lg text-[13px] font-bold flex items-center gap-2 disabled:opacity-60"
              style={{ background: "#0a0a0a", color: "#e8c873" }}
              data-testid="contact-save-btn"
            >
              <MailCheck className="w-4 h-4" />
              {saving
                ? (lang === "ar" ? "جارٍ الحفظ…" : "Saving…")
                : (lang === "ar" ? "حفظ بيانات الاتصال" : "Save contact")}
            </button>
          </div>
        )}
      </StepCard>

      {/* ── Step 3: Documents ───────────────────────────────────── */}
      <StepCard
        num={3}
        titleAr="المستندات المطلوبة"
        titleEn="Required documents"
        done={checklist.steps[2].done}
        testid="verification-step-documents"
      >
        <div className="space-y-3">
          {(() => {
            // Baseline required docs + category-specific required docs
            // (e.g. car showrooms must upload national_address_proof)
            // + the recommended list.  Order preserves the sequence
            // baseline → category-specific → recommended without
            // producing duplicates.
            const catExtras = CATEGORY_REQUIRED_DOCS[business?.category] || [];
            const seen = new Set();
            const rows = [];
            for (const t of REQUIRED_DOCS)      { if (!seen.has(t)) { rows.push({ t, req: true });  seen.add(t); } }
            for (const t of catExtras)          { if (!seen.has(t)) { rows.push({ t, req: true });  seen.add(t); } }
            for (const t of RECOMMENDED_DOCS)   { if (!seen.has(t)) { rows.push({ t, req: false }); seen.add(t); } }
            return rows;
          })().map(({ t, req }) => {
            const uploads = uploadedByType.get(t) || [];
            const has = uploads.length > 0;
            return (
              <div
                key={t}
                className="rounded-xl border p-3 flex flex-wrap items-start gap-3"
                style={{ borderColor: has ? "#c9a55c33" : "#0a0a0a10", background: has ? "#fdf9ef" : "#fff" }}
                data-testid={`doc-row-${t}`}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: has ? "#c9a55c22" : "#f0eee7" }}
                >
                  {has ? <Check className="w-5 h-5" style={{ color: "#c9a55c" }} /> : <FileText className="w-5 h-5 text-[#8a8a8a]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-[#0a0a0a]">
                    {lang === "ar" ? DOC_LABELS[t].ar : DOC_LABELS[t].en}
                    {req && (
                      <span className="text-[10px] font-bold ms-2 px-1.5 py-0.5 rounded" style={{ background: "#fee2e2", color: "#b91c1c" }}>
                        {lang === "ar" ? "إلزامي" : "required"}
                      </span>
                    )}
                  </div>
                  {uploads.map((d) => (
                    <div key={d.id} className="mt-1 text-[11px] text-[#8a8a8a] flex items-center gap-2 flex-wrap">
                      <span className="truncate max-w-[240px]">{d.original_filename}</span>
                      <span>·</span>
                      <span>{(d.size_bytes / 1024).toFixed(0)} KB</span>
                      {d.expires_at && (
                        <>
                          <span>·</span>
                          <span style={{ color: new Date(d.expires_at) < new Date() ? "#dc2626" : "#8a8a8a" }}>
                            {lang === "ar" ? "ينتهي" : "expires"}: {d.expires_at.slice(0, 10)}
                          </span>
                        </>
                      )}
                      {canUpload && (
                        <button
                          type="button"
                          onClick={() => deleteDoc(d.id)}
                          className="text-[#dc2626] hover:underline flex items-center gap-1"
                          data-testid={`doc-delete-${d.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                          {lang === "ar" ? "حذف" : "delete"}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {canUpload && (
                  <div className="shrink-0">
                    <input
                      ref={(el) => { fileInputsRef.current[t] = el; }}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.webp"
                      className="hidden"
                      onChange={(e) => uploadDoc(t, e.target.files?.[0])}
                      data-testid={`doc-input-${t}`}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputsRef.current[t]?.click()}
                      disabled={uploadingType === t}
                      className="px-3 py-1.5 rounded-lg text-[12px] font-bold flex items-center gap-1.5 disabled:opacity-60"
                      style={{ background: "#0a0a0a", color: "#e8c873" }}
                      data-testid={`doc-upload-${t}`}
                    >
                      <Upload className="w-3.5 h-3.5" />
                      {uploadingType === t
                        ? (lang === "ar" ? "جارٍ الرفع…" : "Uploading…")
                        : has
                          ? (lang === "ar" ? "إعادة الرفع" : "Replace")
                          : (lang === "ar" ? "رفع الوثيقة" : "Upload")}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          <div className="text-[11px] text-[#8a8a8a] flex items-center gap-2 pt-1">
            <Info className="w-3.5 h-3.5" />
            {lang === "ar"
              ? "الحد الأقصى لحجم كل وثيقة 15 ميجابايت. الصيغ المدعومة: PDF, PNG, JPG."
              : "Max 15MB per document. Supported: PDF, PNG, JPG."}
          </div>
        </div>
      </StepCard>

      {/* ── Step 4: Submit ──────────────────────────────────────── */}
      <StepCard
        num={4}
        titleAr="إرسال الطلب للمراجعة"
        titleEn="Submit for review"
        done={checklist.steps[3].done}
        testid="verification-step-submit"
      >
        {canSubmit ? (
          <>
            <div className="text-[13px] text-[#525252] mb-3">
              {lang === "ar"
                ? "أكملت جميع المتطلبات. سيبدأ فريق كرتال المراجعة خلال 1–3 أيام عمل."
                : "You've completed all requirements. CARTAL admins will begin review within 1–3 business days."}
            </div>
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="px-5 py-2.5 rounded-lg text-[14px] font-bold flex items-center gap-2 disabled:opacity-60"
              style={{ background: "linear-gradient(135deg,#c9a55c,#e8c873)", color: "#0a0a0a" }}
              data-testid="verification-submit-btn"
            >
              <Send className="w-4 h-4" />
              {submitting
                ? (lang === "ar" ? "جارٍ الإرسال…" : "Submitting…")
                : status === "rejected" || status === "needs_more_docs"
                  ? (lang === "ar" ? "إعادة إرسال الطلب" : "Resubmit for review")
                  : (lang === "ar" ? "إرسال الطلب لإدارة كرتال" : "Submit to CARTAL")}
            </button>
          </>
        ) : status === "submitted" || status === "under_review" ? (
          <div className="text-[13px] text-[#525252] flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#c9a55c]" />
            {lang === "ar"
              ? "طلبك قيد المراجعة. ستصلك إشعارات فور اتخاذ قرار."
              : "Your submission is under review. You'll be notified once a decision is made."}
          </div>
        ) : status === "approved" ? (
          <div className="text-[13px] text-[#16a34a] flex items-center gap-2 font-semibold">
            <ClipboardCheck className="w-4 h-4" />
            {lang === "ar" ? "تمت الموافقة على نشاطك — الوظائف التشغيلية مفعّلة." : "Your business is approved."}
          </div>
        ) : (
          <div className="text-[13px] text-[#8a8a8a]">
            {lang === "ar"
              ? "أكمل الخطوات السابقة أولًا (بيانات الاتصال + السجل التجاري) لتفعيل زر الإرسال."
              : "Complete the previous steps (contact + Commercial Registration) to enable the submit button."}
          </div>
        )}
      </StepCard>

      {/* ── Step 5: Awaiting approval ──────────────────────────── */}
      <StepCard
        num={5}
        titleAr="موافقة إدارة كرتال"
        titleEn="CARTAL admin approval"
        done={checklist.steps[4].done}
        testid="verification-step-approval"
      >
        <div className="text-[13px] text-[#525252] leading-relaxed">
          {status === "approved"
            ? (lang === "ar"
                ? "تم توثيق نشاطك بنجاح ✓ يمكنك الآن نشر المنتجات، تفعيل الخدمات، وربط إعلانات المركبات باسم النشاط."
                : "Your business is verified ✓ You can now publish products, activate services, and link vehicle listings.")
            : (lang === "ar"
                ? "بعد الإرسال، ستراجع الإدارة الوثائق وتصدر قرارها. لا يمكن نشر أو تفعيل أي محتوى تجاري باسم النشاط قبل الموافقة."
                : "After submission, admins will review your documents. No commercial content can be published until approval.")}
        </div>
      </StepCard>
    </div>
  );
}

function StepCard({ num, titleAr, titleEn, done, children, testid }) {
  const { lang } = useI18n();
  return (
    <div
      className="rounded-2xl bg-white border p-5"
      style={{ borderColor: done ? "#16a34a33" : "#0a0a0a10" }}
      data-testid={testid}
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-bold shrink-0"
          style={{
            background: done ? "#16a34a" : "#0a0a0a",
            color: done ? "#fff" : "#e8c873",
          }}
        >
          {done ? <Check className="w-4 h-4" /> : num}
        </div>
        <h3 className="text-[15px] font-bold text-[#0a0a0a]">
          {lang === "ar" ? titleAr : titleEn}
        </h3>
      </div>
      {children}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-[#8a8a8a] font-semibold">{label}</div>
      <div className="text-[13px] text-[#0a0a0a] font-medium mt-0.5 truncate">
        {value || <span className="text-[#c9a55c]">— </span>}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, disabled, type = "text", testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-[#8a8a8a] font-semibold">{label}</span>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="mt-1 w-full rounded-lg border border-[#0a0a0a]/10 bg-white px-3 py-2 text-[13px] text-[#0a0a0a] focus:border-[#c9a55c] focus:outline-none disabled:bg-[#fafaf7] disabled:text-[#8a8a8a]"
        data-testid={testid}
      />
    </label>
  );
}
